import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEvent, getCompany, getSubcontractedEvents } from 'src/api/company';
import { getAllTags } from 'src/api/admin/tags';
import { getEventTypes } from 'src/api/admin/types';
import { useConfig } from 'src/hooks/useConfig';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
import { useBreadcrumbLevel, useLocalStorage } from '@repo/components/hooks';
import { getCompanyEventAccess } from 'src/helpers/companyEventAccess';
export const Route = createFileRoute('/company_/$groupId/events/$eventId/subcontracting')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const { updateValue: setBackUrl } = useLocalStorage<string>('companyPortalEventsBackUrl');

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: subEvents, isLoading: subEventsLoading } = useQuery({
		queryKey: ['company-event-subcontracting', groupId, eventId],
		queryFn: () => getSubcontractedEvents(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: tags } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
	});

	const { data: group } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	const { data: typesResponse } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes('?noPagination=true'),
	});

	useBreadcrumbLevel(3, 'Subcontracting');

	if (userLoading || eventLoading || subEventsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);
	const { isReadOnlyEvent } = getCompanyEventAccess(event, groupId, authenticatedUser?.id);

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-subcontracting', groupId, eventId] });
	};

	const addEvent = async (data: unknown) => {
		await request.post(`/api/company-portal/groups/${groupId}/events/${eventId}/subcontracting`, data);
	};

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			groupId={groupId}
			subContractingGroup={group}
			events={subEvents}
			addEvent={addEvent}
			onReload={handleReload}
			types={typesResponse?.data || []}
			parentEventId={eventId}
			parentEvent={event}
			permissions={{
				view: verifyPermission(PERMISSIONS.companyEventsView),
				create:
					!isReadOnlyEvent && group?.legalForm !== 'selfEmployed' && verifyPermission(PERMISSIONS.companyEventsCreate),
				update: !isReadOnlyEvent && verifyPermission(PERMISSIONS.companyEventsEdit),
				archive: false,
			}}
			usesTags={featureFlags?.USES_TAGS || false}
			tags={tags || []}
			eventDetailUrl={`/company/${groupId}/events`}
			onEventDetailClick={() => setBackUrl(window.location.href)}
			isCompanyPortalEvent={true}
			project={event?.project}
		/>
	);
}
